import "./FormTracker.scss";
import React from "react";
import { Forward } from "../../../assets/svg";
import type { FormTrackerProps, FormTrackerStepStatus } from "./types";

const getStepStatus = (stepIndex: number, currentStep: number): FormTrackerStepStatus => {
  const stepNumber = stepIndex + 1;
  if (stepNumber < currentStep) return "complete";
  if (stepNumber === currentStep) return "active";
  return "pending";
};

const badgeClassName = (status: FormTrackerStepStatus) => {
  if (status === "active") return "form-tracker__badge form-tracker__badge--active";
  if (status === "complete") return "form-tracker__badge form-tracker__badge--complete";
  return "form-tracker__badge";
};

const countClassName = (status: FormTrackerStepStatus) => {
  if (status === "active") return "form-tracker__count form-tracker__count--active";
  if (status === "complete") return "form-tracker__count form-tracker__count--complete";
  return "form-tracker__count";
};

export const FormTracker = (props: FormTrackerProps) => {
  const { steps, currentStep, onStepClick, className } = props;

  if (!steps?.length) return null;

  const rootClassName = ["form-tracker__steps", className].filter(Boolean).join(" ");

  return (
    <div className={rootClassName} role="navigation" aria-label="Form steps">
      {steps.map((item, index) => {
        const status = getStepStatus(index, currentStep);
        const stepNumber = index + 1;
        const isClickable = Boolean(onStepClick) && (status === "complete" || status === "active");

        return (
          <React.Fragment key={item.id ?? item.title}>
            <div className="form-tracker__step" aria-current={status === "active" ? "step" : undefined}>
              <div
                className="form-tracker__step-body"
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? () => onStepClick?.(stepNumber) : undefined}
                onKeyDown={
                  isClickable
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onStepClick?.(stepNumber);
                        }
                      }
                    : undefined
                }
                style={isClickable ? { cursor: "pointer" } : undefined}
              >
                <div className={badgeClassName(status)}>
                  <p className={countClassName(status)}>{stepNumber}</p>
                </div>
                <div className="form-tracker__text">
                  <p className="form-tracker__title">{item.title}</p>
                  <p className="form-tracker__label">{item.label}</p>
                </div>
              </div>
            </div>

            {index !== steps.length - 1 ? (
              <div className="form-tracker__chevron" aria-hidden>
                <Forward />
              </div>
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};
